(() => {
  const CART_EMPTY_DRAWER_CLASS = 'drawer--empty-with-recommendations';

  function syncCartDrawerEmptyState(drawer, resetScroll = false) {
    if (!drawer) {
      return;
    }

    const isEmpty = Boolean(drawer.querySelector('[data-cart-empty-content]'));
    drawer.classList.toggle(CART_EMPTY_DRAWER_CLASS, isEmpty);

    if (!resetScroll) {
      return;
    }

    const body = drawer.shadowRoot?.querySelector('[part~="body"]');

    if (body) {
      body.scrollTop = 0;
    }
  }

  document.addEventListener('cart-drawer:refreshed', (event) => {
    const drawer = event.target?.closest?.('cart-drawer');

    requestAnimationFrame(() => syncCartDrawerEmptyState(drawer, true));
  });

  document.addEventListener('dialog:before-show', (event) => {
    const drawer = event.target?.closest?.('cart-drawer');

    if (drawer) {
      syncCartDrawerEmptyState(drawer, true);
    }
  });

  customElements.whenDefined('cart-drawer').then(() => {
    document.querySelectorAll('cart-drawer').forEach((drawer) => {
      syncCartDrawerEmptyState(drawer, true);
    });
  });

  if (!customElements.get('cart-empty-recommendations')) {
    class CartEmptyRecommendations extends HTMLElement {
      connectedCallback() {
        syncCartDrawerEmptyState(this.closest('cart-drawer'));

        this.grid = this.querySelector('[data-product-grid]');
        this.sentinel = this.querySelector('[data-load-more-sentinel]');
        this.errorMessage = this.querySelector('[data-load-error]');
        this.collectionUrl = this.dataset.collectionUrl;
        this.page = 1;
        this.loading = false;
        this.hasMore = Boolean(this.grid && this.sentinel && this.collectionUrl);
        this.seenHandles = new Set(
          Array.from(this.querySelectorAll('product-card[handle]'))
            .map((card) => card.getAttribute('handle'))
            .filter(Boolean)
        );

        if (!this.hasMore) {
          return;
        }

        if (!('IntersectionObserver' in window)) {
          this.loadNextPage();
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
              this.loadNextPage();
            }
          },
          { rootMargin: '240px 0px' }
        );

        this.observer.observe(this.sentinel);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      async loadNextPage() {
        if (this.loading || !this.hasMore) {
          return;
        }

        this.loading = true;
        this.classList.add('is-loading');
        this.observer?.unobserve(this.sentinel);

        try {
          const url = new URL(this.collectionUrl, window.location.origin);
          url.searchParams.set('page', String(this.page));

          const response = await fetch(url.toString(), {
            headers: { 'X-Requested-With': 'XMLHttpRequest' }
          });

          if (!response.ok) {
            throw new Error(`Unable to load recommendations (${response.status})`);
          }

          const html = await response.text();
          const documentFragment = new DOMParser().parseFromString(html, 'text/html');
          const sourceGrid = documentFragment.querySelector('product-list.product-list');

          if (!sourceGrid) {
            throw new Error('Recommendation product grid was not found');
          }

          const cards = Array.from(sourceGrid.children).filter((node) =>
            node.matches('product-card[handle]')
          );

          cards.forEach((card) => {
            const handle = card.getAttribute('handle');

            if (!handle || this.seenHandles.has(handle)) {
              return;
            }

            const importedCard = document.importNode(card, true);
            importedCard.removeAttribute('reveal-on-scroll');
            importedCard
              .querySelectorAll('product-form, quick-buy-modal, .product-card__quick-add-button')
              .forEach((element) => element.remove());
            this.grid.append(importedCard);
            this.seenHandles.add(handle);
          });

          this.hasMore = Boolean(documentFragment.querySelector('a[rel="next"]'));
          this.page += 1;
        } catch (error) {
          this.hasMore = false;

          if (this.errorMessage) {
            this.errorMessage.hidden = false;
          }

          console.warn('[cart recommendations]', error);
        } finally {
          this.loading = false;
          this.classList.remove('is-loading');

          if (this.hasMore) {
            this.observer?.observe(this.sentinel);
          } else {
            this.sentinel?.remove();
          }
        }
      }
    }

    customElements.define('cart-empty-recommendations', CartEmptyRecommendations);
  }
})();
